import { useTranslation } from "react-i18next";
import i18n from "src/i18n";
import { useDraftCAIStore } from "src/storage/formik/formik-draft.store";
import {
  AccidentCircumstanceKeyEnum,
  AuthorityType,
  EsitoResponsabilita,
  FormikDraftCAI,
  InjuredType,
  InputSelectOption,
  VehicleOwnerType,
  YesNoAnswer,
} from "src/types";

export const useVehicleOwnerTypeOptions = () => {
  const { t } = useTranslation();
  const vehicleOwnerTypeOptions: InputSelectOption<VehicleOwnerType>[] = [
    {
      id: VehicleOwnerType.NATURAL,
      value: t("commons.natural_person"),
    },
    {
      id: VehicleOwnerType.LEGAL,
      value: t("commons.legal_person"),
    },
  ];

  return { vehicleOwnerTypeOptions };
};

export const useInjuredTypeOptions = () => {
  const { t } = useTranslation();
  const injuredTypeOptions: InputSelectOption<InjuredType>[] = [
    {
      id: InjuredType.DRIVER_A,
      value: t("commons.driver_a"),
    },
    {
      id: InjuredType.DRIVER_B,
      value: t("commons.driver_b"),
    },
    {
      id: InjuredType.PASSENGER_A,
      value: t("commons.passenger_a"),
    },
    {
      id: InjuredType.PASSENGER_B,
      value: t("commons.passenger_b"),
    },
    {
      id: InjuredType.WALKER,
      value: t("commons.walker"),
    },
  ];

  return { injuredTypeOptions };
};

export const useAuthorityTypeOptions = () => {
  const { t } = useTranslation();
  const authorityTypeOptions: InputSelectOption<AuthorityType>[] = [
    {
      id: AuthorityType.CARABINIERI,
      value: t("commons.carabinieri"),
    },
    {
      id: AuthorityType.STATE_POLICE,
      value: t("commons.state_police"),
    },
    {
      id: AuthorityType.MUNICIPAL_POLICE,
      value: t("commons.local_police"),
    },
  ];

  return { authorityTypeOptions };
};

export const useYesNoAnswer = () => {
  const { t } = useTranslation();
  const yesNoOptions: InputSelectOption<YesNoAnswer>[] = [
    { id: YesNoAnswer.YES, value: t("commons.yes") },
    { id: YesNoAnswer.NO, value: t("commons.no") },
  ];

  return { yesNoOptions };
};

export const useAccidentCircumstancesOptions = () => {
  const { t } = useTranslation();

  const accidentCircumstancesOptions: InputSelectOption<AccidentCircumstanceKeyEnum>[] =
    [
      {
        id: AccidentCircumstanceKeyEnum.PARKED_OR_STOPPED,
        value: t(
          "data_entry.accident_step.section_accident_circumstances_labels.parkedOrStopped",
        ),
      },
      {
        id: AccidentCircumstanceKeyEnum.MOVING_OFF_AFTER_STOP,
        value: t(
          "data_entry.accident_step.section_accident_circumstances_labels.movingOffAfterStop",
        ),
      },
      {
        id: AccidentCircumstanceKeyEnum.PARKING,
        value: t(
          "data_entry.accident_step.section_accident_circumstances_labels.parking",
        ),
      },
      {
        id: AccidentCircumstanceKeyEnum.LEAVING_PARKING,
        value: t(
          "data_entry.accident_step.section_accident_circumstances_labels.leaving",
        ),
      },
      {
        id: AccidentCircumstanceKeyEnum.ENTERING_PARKING,
        value: t(
          "data_entry.accident_step.section_accident_circumstances_labels.entering",
        ),
      },

      {
        id: AccidentCircumstanceKeyEnum.ENTERING_SQUARE,
        value: t(
          "data_entry.accident_step.section_accident_circumstances_labels.enteringSquare",
        ),
      },
      {
        id: AccidentCircumstanceKeyEnum.DRIVING_IN_SQUARE,
        value: t(
          "data_entry.accident_step.section_accident_circumstances_labels.circularTraffic",
        ),
      },
      {
        id: AccidentCircumstanceKeyEnum.REAR_ENDING_SAME_DIRECTION,
        value: t(
          "data_entry.accident_step.section_accident_circumstances_labels.sameDirectionRearEnd",
        ),
      },
      {
        id: AccidentCircumstanceKeyEnum.MOVING_SAME_DIRECTION,
        value: t(
          "data_entry.accident_step.section_accident_circumstances_labels.movingSameDirectionDifferentLane",
        ),
      },
      {
        id: AccidentCircumstanceKeyEnum.CHANGING_LANE,
        value: t(
          "data_entry.accident_step.section_accident_circumstances_labels.changingLane",
        ),
      },
      {
        id: AccidentCircumstanceKeyEnum.OVERTAKING,
        value: t(
          "data_entry.accident_step.section_accident_circumstances_labels.overtaking",
        ),
      },
      {
        id: AccidentCircumstanceKeyEnum.TURNING_RIGHT,
        value: t(
          "data_entry.accident_step.section_accident_circumstances_labels.turningRight",
        ),
      },
      {
        id: AccidentCircumstanceKeyEnum.TURNING_LEFT,
        value: t(
          "data_entry.accident_step.section_accident_circumstances_labels.turningLeft",
        ),
      },
      {
        id: AccidentCircumstanceKeyEnum.REVERSING,
        value: t(
          "data_entry.accident_step.section_accident_circumstances_labels.retreating",
        ),
      },
      {
        id: AccidentCircumstanceKeyEnum.INVADING_OPPOSITE_LANE,
        value: t(
          "data_entry.accident_step.section_accident_circumstances_labels.invadingOppositeLane",
        ),
      },
      {
        id: AccidentCircumstanceKeyEnum.COMING_FROM_RIGHT,
        value: t(
          "data_entry.accident_step.section_accident_circumstances_labels.comingFromRight",
        ),
      },
      {
        id: AccidentCircumstanceKeyEnum.NO_RIGHT_OF_WAY_OR_RED_LIGHT,
        value: t(
          "data_entry.accident_step.section_accident_circumstances_labels.noRightOfWayOrRedLight",
        ),
      },
    ];

  return { accidentCircumstancesOptions };
};

export const mapDriverResponsabilitiesResponse = ({
  response,
  formikCai,
}: {
  response: EsitoResponsabilita;
  formikCai: FormikDraftCAI;
}): string => {
  const plateA = formikCai.vehicleA.vehicleInfo.plate;
  const plateB = formikCai.vehicleB.vehicleInfo.plate;

  switch (response) {
    case EsitoResponsabilita.REASON:
      return i18n.t(
        "data_entry.accident_step.section_accident_circumstances_labels.resp_reason",
        { plate: plateB },
      );

    case EsitoResponsabilita.WRONG:
      return i18n.t(
        "data_entry.accident_step.section_accident_circumstances_labels.resp_reason",
        { plate: plateA },
      );

    case EsitoResponsabilita.CONTEST:
      return i18n.t(
        "data_entry.accident_step.section_accident_circumstances_labels.resp_contest",
        {
          plate_a: plateA,
          plate_b: plateB,
        },
      );
    case EsitoResponsabilita.NOT_CODIFIABLE:
      return i18n.t(
        "data_entry.accident_step.section_accident_circumstances_labels.resp_not_codifiable",
      );
    default:
      return "";
  }
};
